import { type Dispatch, type FC, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import StarIcon from '@assets/img/icons/star.svg?react';
import { AnimatePresence, motion } from 'framer-motion';

import { useGetLeagueRewardMutation } from '@/api/queries/league';
import { opacityAnimation } from '@/common/config/motion-animations';
import { entriesToSingleFieldObjArray } from '@/common/lib/array';
import { SendAnalitics } from '@/common/lib/playdeck';
import { toShortNumFormat } from '@/common/lib/toShortNumFormat';
import { type LeagueMyRewardType } from '@/common/types/league';
import { largeChestImages } from '@/components/Leagues/config';
import { getChestKindByLeaguePosition } from '@/components/Leagues/lib';
import { RewardItem } from '@/components/Leagues/RewardItem/RewardItem';
import { WinnerPosition } from '@/components/Leagues/WinnerPosition/WinnerPosition';
import { H1, StringSmall } from '@/components/Typography/Typography';
import { MainButton } from '@/components/ui/Button/Button';
import { GlowEffect } from '@/components/ui/GlowEffect/GlowEffect';
import { Picture } from '@/components/ui/Picture/Picture';
import { clearMyRewardsByLeagueId } from '@/redux/reducers/leaguesReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import css from './styles.module.scss';

import starsPng from '/img/modals/stars-1.png';
import starsWebp from '/img/modals/stars-1.webp';

interface LeagueClaimRewardProps {
  isOpen: boolean;
  rewards?: LeagueMyRewardType;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setRewardIndex: Dispatch<SetStateAction<number>>;
  setMyRewards: Dispatch<SetStateAction<LeagueMyRewardType[]>>;
  leagueShortInfo: { name: string; num: number } | null;
  rewardsQuantity: number;
  rewardIndex: number;
  leagueId?: string;
}

const LeagueClaimReward: FC<LeagueClaimRewardProps> = ({
  isOpen,
  rewards,
  setRewardIndex,
  leagueShortInfo,
  rewardsQuantity,
  rewardIndex,
  setIsOpen,
  setMyRewards,
  leagueId,
}) => {
  const { t } = useTranslation();
  const myAvatarUrl = useAppSelector((state) => state.user.user?.photoUrl);
  const [getReward, { isLoading }] = useGetLeagueRewardMutation();
  const dispatch = useAppDispatch();

  const kind = getChestKindByLeaguePosition(rewards?.ranking);

  const chestImage = kind ? largeChestImages[kind] : undefined;

  const onClick = (): void => {
    const isLast = rewardsQuantity - 1 === rewardIndex;

    if (rewards) {
      getReward({ leagueHistoryId: rewards.leagueHistoryId }).then(() => {
        if (rewards.ranking <= 20 && leagueShortInfo) {
          const { wallet, materials, lotteries, lottery, starPoints } = rewards;
          SendAnalitics.leaderboardWin({
            league: leagueShortInfo.name,
            league_num: leagueShortInfo.num,
            place: rewards.ranking,
            reward: Object.entries({
              ...wallet,
              ...lotteries,
              ...lottery,
              starPoints,
              materials,
            }).map(entriesToSingleFieldObjArray),
          });
        }

        if (isLast) {
          setIsOpen(false);
          setRewardIndex(0);
          setMyRewards([]);

          if (leagueId) {
            dispatch(clearMyRewardsByLeagueId({ leagueId }));
          }
        } else {
          setRewardIndex((state) => state + 1);
        }
      });
    }
  };

  return (
    <AnimatePresence>
      {isOpen && rewards && (
        <motion.div className={css.claimModal} {...opacityAnimation}>
          <H1 weight="bold" className="mb-4">
            {t('Congrats!')}
          </H1>
          {myAvatarUrl && (
            <div className="flex items-center flex-col justify-end h-[4.625rem]">
              <WinnerPosition
                size="large"
                className="mb-1"
                position={rewards.ranking}
                avatar={myAvatarUrl}
              />
            </div>
          )}
          <div className="flex items-center gap-1 mb-4">
            <StarIcon className="text-gold" />
            <StringSmall weight="bold">
              {toShortNumFormat(rewards.starPoints)}
            </StringSmall>
          </div>
          <StringSmall weight="regular" className="!text-white/60 mb-6">
            {t('You took {{position}}st place in the event', {
              position: rewards.ranking,
            })}
          </StringSmall>
          <div className={css.rewards}>
            {[
              ...Object.entries(rewards.wallet),
              ...Object.entries(rewards.lottery || rewards.lotteries || {}),
              ...Object.entries(rewards.materials),
            ].map(([item, quantity]) => (
              <RewardItem
                key={`reward-${item}`}
                item={item}
                quantity={quantity}
              />
            ))}
          </div>
          <div className="w-[12.5rem] h-[12.5rem] relative mb-10">
            <GlowEffect className="w-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 -z-[1]" />
            <div
              style={{
                backgroundImage: `image-set("${starsPng}" type("image/png"), "${starsWebp}" type("image/webp"))`,
              }}
              className={css.stars}
            ></div>
            <Picture
              key={rewards.leagueHistoryId}
              img={chestImage?.png}
              webp={chestImage?.webp}
              className="w-full h-full"
              alt="chest"
            />
          </div>
          <MainButton
            disabled={isLoading}
            isLoading={isLoading}
            onClick={onClick}
            text={`${t('Claim')}${rewardsQuantity > 1 ? ' ' + (rewardIndex + 1) + '/' + rewardsQuantity : ''}`}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { LeagueClaimReward };
